/**
 * Sentry — admin portal client-side error capture.
 *
 * The admin portal is App-Owner-only, so it sees fewer sessions per
 * day than the customer portal — but each error here matters more
 * (an operator mid-refund hits a bug and we need to know within
 * minutes).
 *
 * Identical to `web-customer/src/lib/sentry.ts`; kept as a separate
 * module to allow per-project DSN / sample-rate tuning without
 * touching the other project's bundle.
 */
import * as Sentry from '@sentry/react';

let initialised = false;

export function initAdminSentry(): void {
  if (initialised) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    console.warn(
      '[sentry] VITE_SENTRY_DSN not set — error monitoring is disabled.',
    );
    initialised = true;
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Admin sessions handle PII (org names, owner contacts).
        // Mask everything by default; the on-call engineer can
        // still see the click flow + the failing API response in
        // the breadcrumbs/network panel.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // 100% trace sampling — admin volume is tiny, traces help us
    // understand slow OrgDetail / Subscribers paginations.
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE ?? undefined,
    sendDefaultPii: false,
  });
  initialised = true;
}

/** Attach the App-Owner uid to every event after sign-in. */
export function setSentryUser(uid: string | null, email?: string | null): void {
  if (!initialised) return;
  Sentry.setUser(
    uid ? { id: uid, email: email ?? undefined } : null,
  );
}

export function captureAdminError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialised) {
    console.error('[error]', error, context);
    return;
  }
  Sentry.withScope((scope) => {
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        scope.setExtra(key, value);
      }
    }
    Sentry.captureException(error);
  });
}
